import type { Tickets } from '../api/types';
import { FunnelChart } from '../charts/charts';
import { Async, PanelCard } from '../components/Panel';
import { Signal } from '../components/Signal';
import { ClampedText, Muted, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num, pct, plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';

/**
 * The funnel as four figures, with the two steps that fail quietly given tiles of their own.
 * An undelivered ticket is somebody still waiting, having been told their request was sent,
 * so it is the one figure allowed a colour — with the hint beside it saying so in words.
 */
function tilesFor(data: Tickets, word: (full: string, plain: string) => string): TileSpec[] {
  const missing = Math.max(0, data.filed - data.delivered);
  // Null, not zero, when the service answering is older than this field: "0% of offers were
  // accepted" is a finding and must not be invented from a missing number.
  const afterOffer = data.filed_after_offer == null ? null : Number(data.filed_after_offer);
  const sought = afterOffer == null ? null : Math.max(0, data.filed - afterOffer);

  return [
    {
      label: word('Tickets filed', 'Support tickets filed'),
      value: num(data.filed),
      hint: !data.filed
        ? `nobody filed one, in ${plural(data.conversations, 'conversation')}`
        : afterOffer == null
          ? `across ${plural(data.conversations, 'conversation')}`
          : `${num(afterOffer)} after an offer, ${num(sought)} asked for outright`,
    },
    {
      // Counted from the tickets that followed an offer, not from every ticket: one sought
      // out independently is not an offer that worked.
      label: word('Offers taken up', 'Offers people accepted'),
      value: pct(data.suggested && afterOffer != null ? afterOffer / data.suggested : null),
      hint: data.suggested
        ? `the assistant offered a ticket in ${plural(data.suggested, 'conversation')} of ${num(data.conversations)}`
        : 'the assistant never offered one',
    },
    {
      label: word('Undelivered', 'Never arrived'),
      value: num(missing),
      tone: missing ? 'critical' : undefined,
      hint: missing
        ? `${pct(data.filed ? missing / data.filed : null)} of those filed — each one is somebody who was told their request had been sent`
        : 'every ticket reached the helpdesk',
    },
    {
      label: word('Turns before escalating', 'Questions before asking a human'),
      value: num(data.avg_turns_before, 1),
      hint: 'how long people try the assistant before giving up on it',
    },
  ];
}

export function TicketsPanel() {
  const { days, system, word } = useApp();
  const query = useApi<Tickets>('/api/tickets', { days, system });

  return (
    <Async query={query} shape="page">
      {(data) => (
        <div className="space-y-5">
          <TileRow tiles={tilesFor(data, word)} />

          <PanelCard
            title="Escalation funnel"
            simpleTitle="From conversation to support ticket"
            hint="Conversations → suggested → filed → delivered. The last step is the one nobody notices when it breaks."
            simpleHint="How many were offered a ticket, accepted, and reached the helpdesk. The last step fails silently."
          >
            <FunnelChart
              label="Escalation funnel, from conversations to delivered tickets"
              steps={[
                { label: 'Conversations', value: data.conversations },
                { label: 'Ticket suggested', value: data.suggested },
                { label: 'Ticket filed', value: data.filed },
                { label: 'Delivered', value: data.delivered },
              ]}
            />
          </PanelCard>

          {/* The tickets themselves, where a bar chart of categories used to be. At this
              volume that chart was one bar saying "administrative 1", and the category is
              worth more as a column beside the request it describes than as a chart of its
              own. */}
          <PanelCard
            title="Every ticket"
            simpleTitle="The tickets people filed"
            hint="What was asked for — in the person's own wording — what the assistant had been asked just before, and whether it arrived."
            simpleHint="Every support ticket in this period: what the person wanted, the last thing they had asked the assistant, and whether it reached the helpdesk."
          >
            <Table
              rows={data.items}
              pageSize={10}
              search="Search tickets"
              // Two different silences, and only one is a fact about the period: a service
              // older than this panel sends no list at all.
              empty={
                data.items == null
                  ? 'The service answering is older than this panel — restart it to list the tickets.'
                  : 'Nobody filed a support ticket in this period.'
              }
              columns={[
                { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                {
                  id: 'title',
                  label: 'Asked for',
                  sortValue: (r) => r.title,
                  render: (r) => <ClampedText text={r.title} missing={<Muted>no title</Muted>} />,
                },
                {
                  // The gap this ticket is: what the assistant was asked immediately before
                  // the person gave up on it.
                  id: 'query',
                  label: 'Last asked',
                  simple: 'What they asked first',
                  sortValue: (r) => r.query,
                  render: (r) => (
                    <ClampedText text={r.query} missing={<Muted>not recorded</Muted>} />
                  ),
                },
                { key: 'category', label: 'Category' },
                { key: 'priority', label: 'Priority', advanced: true },
                { key: 'user_role', label: 'Role' },
                {
                  id: 'turns_before',
                  label: 'Turns first',
                  simple: 'Questions first',
                  num: true,
                  advanced: true,
                  render: (r) => num(r.turns_before),
                  sortValue: (r) => r.turns_before,
                },
                {
                  // Whether the assistant proposed it or the person went looking for it. A
                  // page of "asked for it" means the offer is not doing its job.
                  id: 'was_suggested',
                  label: 'How',
                  sortValue: (r) => String(r.was_suggested),
                  render: (r) =>
                    r.was_suggested ? word('offered', 'the assistant offered') : word('sought', 'they asked for it'),
                },
                {
                  id: 'delivered',
                  label: 'Arrived',
                  sortValue: (r) => String(r.delivered),
                  render: (r) =>
                    r.delivered ? (
                      <Signal tone="good">✓ delivered</Signal>
                    ) : (
                      <Signal tone="critical">✕ {r.delivery_status || 'never arrived'}</Signal>
                    ),
                },
                {
                  // The helpdesk's own id: the string to paste over there to find it.
                  id: 'reference',
                  label: 'Reference',
                  advanced: true,
                  sortValue: (r) => r.reference,
                  render: (r) => r.reference || r.ticket_id || <Muted>—</Muted>,
                },
              ]}
            />
          </PanelCard>

          <PanelCard
            title="Undelivered tickets"
            simpleTitle="Tickets that never arrived"
            hint="Generated but never reached the helpdesk. The user was told it was sent."
            simpleHint="Created but never arrived, and the person was told it had been. Each row is somebody still waiting."
          >
            <Table
              rows={data.failures}
              // Twenty is what the endpoint returns, and a run of undelivered tickets is
              // exactly when this table is longest.
              pageSize={10}
              empty="Every ticket in this window reached the helpdesk."
              columns={[
                { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                { key: 'ticket_id', label: 'Ticket' },
                { key: 'delivery_status', label: 'Status' },
                { key: 'system', label: 'System' },
              ]}
            />
          </PanelCard>
        </div>
      )}
    </Async>
  );
}
